import { useState, useRef, MouseEvent, useMemo } from 'react';
import {
  Box,
  Button,
  Popper,
  Paper,
  MenuItem,
  Typography,
  Badge,
  useTheme,
} from '@mui/material';
import { menuConstants } from '@constants/menuConstants';
import { useLocation } from 'react-router-dom';
import { useFetchMessageCount } from '@hooks/api/useMessageApi';
import { PrivateRoutesTitles } from '@enums/privateRoutesTitles';

interface MenuItem {
  key: string;
  title: string;
  path?: string;
  icon?: boolean;
  children?: MenuItem[];
  parentKey?: string;
}

interface DesktopMenuProps {
  onMenuClick: (path?: string) => void;
}

const DesktopMenu = ({ onMenuClick }: DesktopMenuProps) => {
  const [hoveredItem, setHoveredItem] = useState<MenuItem | null>(null);
  const [anchorElHover, setAnchorElHover] = useState<null | HTMLElement>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const location = useLocation();
  const theme = useTheme();
  const { data: unreadMessageCount } = useFetchMessageCount();

  const isActiveRoute = (path?: string) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const handleMenuItemHover = (
    event: MouseEvent<HTMLElement>,
    item: MenuItem
  ) => {
    if (item.children) {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      setHoveredItem(item);
      setAnchorElHover(event.currentTarget);
    }
  };

  const handleMenuMouseLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setHoveredItem(null);
      setAnchorElHover(null);
    }, 220);
  };

  const handleMenuClick = (item: MenuItem, event?: MouseEvent<HTMLElement>) => {
    if (item.children) {
      if (event) {
        setAnchorElHover(event.currentTarget);
      }
    } else if (item.path) {
      onMenuClick(item.path);
    }
  };

  const handleSubMenuClick = (path: string) => {
    setAnchorElHover(null);
    onMenuClick(path);
  };

  const menuItems = useMemo(
    () =>
      menuConstants.map(item => {
        const isMessagesItem = item.key === PrivateRoutesTitles.MESSAGES;

        return (
          <Button
            key={item.key}
            onClick={e => handleMenuClick(item, e)}
            onMouseEnter={e => handleMenuItemHover(e, item)}
            onMouseLeave={handleMenuMouseLeave}
            className={isActiveRoute(item.path) ? 'active' : ''}
            sx={{
              color: '#52595F',
              display: 'flex',
              alignItems: 'center',
              outline: 'none !important',
              fontFamily: 'Epilogue',
              fontSize: '16px',
              fontStyle: 'normal',
              lineHeight: '150%',
              padding: '8px 16px',
              '&:focus': {
                outline: 'none !important',
                boxShadow: 'none !important',
              },
              '&:hover': {
                backgroundColor: 'teal.300',
              },
              '&.active': {
                color: 'primary.main',
                backgroundColor: 'primary.light',
                borderRadius: '8px !important',
                fontWeight: 500,
                '&:hover': {
                  backgroundColor: 'primary.light',
                  color: 'primary.main',
                },
              },
            }}
          >
            {isMessagesItem ? (
              <Badge
                badgeContent={unreadMessageCount || 0}
                color="primary"
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.75rem',
                    minWidth: '18px',
                    height: '18px',
                    borderRadius: '9px',
                    fontWeight: 600,
                    backgroundColor: theme.palette.teal[500],
                  },
                }}
              >
                {item.title}
              </Badge>
            ) : (
              item.title
            )}
          </Button>
        );
      }),
    [
      handleMenuClick,
      handleMenuItemHover,
      handleMenuMouseLeave,
      location.pathname,
      unreadMessageCount,
      theme.palette.teal,
    ]
  );

  return (
    <>
      <Box
        sx={{
          position: 'absolute',
          left: '50%',
          transform: 'translateX(-50%)',
          display: { xs: 'none', md: 'none', lg: 'flex' },
          gap: 1,
          justifyContent: 'center',
        }}
      >
        {menuItems}
      </Box>
      <Popper
        open={Boolean(hoveredItem && anchorElHover)}
        anchorEl={anchorElHover}
        placement="bottom"
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 4],
            },
          },
        ]}
        sx={{
          zIndex: 1300,
          display: { xs: 'none', md: 'none', lg: 'block' },
        }}
        onMouseEnter={() => {
          if (closeTimeoutRef.current) {
            clearTimeout(closeTimeoutRef.current);
          }
        }}
        onMouseLeave={handleMenuMouseLeave}
      >
        <Paper
          sx={{
            minWidth: 180,
            transform: 'translateX(-50%)',
            marginLeft: '50%',
            mt: 0.5,
          }}
        >
          {hoveredItem?.children?.map(subItem => (
            <MenuItem
              key={subItem.key}
              onClick={() => handleSubMenuClick(subItem.path || '')}
              sx={{
                '&:hover': {
                  backgroundColor: 'teal.300',
                  color: 'white',
                  borderRadius: '3px',
                },
              }}
            >
              <Typography variant="body1" textAlign="center">
                {subItem.title}
              </Typography>
            </MenuItem>
          ))}
        </Paper>
      </Popper>
    </>
  );
};

export default DesktopMenu;
